import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'

// IST Date helpers - DB dates are already in IST, parse directly without timezone conversion
// This prevents browser from converting to local timezone (which shifts dates for US users)

// Format a date string to display format (DD/MM/YYYY) - parses date parts directly
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    // Extract date parts directly from string to avoid timezone conversion
    const dateOnly = dateString.split('T')[0] // Handle ISO format "2024-01-15T00:00:00"
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

// Convert a date string to YYYY-MM-DD format for form inputs - parses directly
const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    // Extract date parts directly from string to avoid timezone conversion
    const dateOnly = dateString.split('T')[0] // Handle ISO format
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    // Handle other formats if needed
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

type TopupPlan = {
  grade: string
  name: string
  sumInsured: number
  premium: number
  premiumExtended?: number // For Grade 1 with > 3 dependents
}

const RELATIONS = [
  'Spouse',
  'Child',
  'Sibling',
  'Parent',
  'ParentInLaw'
]

const GENDERS = ['Male', 'Female', 'Other']

const PREMIUM_PER_LIFE = 9626
const GST_RATE = 0.18

const TOPUP_PLANS: TopupPlan[] = [
  // Grade 1
  { grade: 'Grade1', name: 'Topup1', sumInsured: 200000, premium: 6407, premiumExtended: 8009 },
  { grade: 'Grade1', name: 'Topup2', sumInsured: 300000, premium: 9611, premiumExtended: 11213 },
  { grade: 'Grade1', name: 'Topup3', sumInsured: 400000, premium: 14010, premiumExtended: 16019 },
  
  // Grade 2
  { grade: 'Grade2', name: 'Topup4', sumInsured: 200000, premium: 8009 },
  { grade: 'Grade2', name: 'Topup5', sumInsured: 300000, premium: 11213 },
  { grade: 'Grade2', name: 'Topup6', sumInsured: 500000, premium: 16019 },
  
  // Grade 3
  { grade: 'Grade3', name: 'Topup7', sumInsured: 200000, premium: 8009 },
  { grade: 'Grade3', name: 'Topup8', sumInsured: 500000, premium: 16019 },
  { grade: 'Grade3', name: 'Topup9', sumInsured: 750000, premium: 22335 },
]

export const OnityEnrollmentForm: React.FC<Props> = ({ policyData, policyEffectiveDate, policyExpiryDate, onSuccess, preloadedMembers, initialDependents }) => {
  const prorationDebugEnabled = (() => {
    if (process.env.NODE_ENV === 'production') return false
    try {
      const flag = typeof window !== 'undefined' ? window.localStorage?.getItem('debugProration') : null
      // Default to enabled in dev unless explicitly disabled
      if (flag === null) return true
      return flag === '1' || flag.toLowerCase() === 'true'
    } catch {
      return true
    }
  })()

  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }

    // DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }

    // DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }

    // Fallback: try to normalize using existing helper (best-effort)
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }

    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const resolveDojRaw = useCallback((): {dojRaw: any; source: string} => {
    // Contract: during open enrollment, DOJ comes only as JoiningDate/joiningdate in the JSON response.
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) return {dojRaw: row.JoiningDate, source: `initialDependents[${i}].JoiningDate`}
        if (row?.joiningdate != null) return {dojRaw: row.joiningdate, source: `initialDependents[${i}].joiningdate`}
        if (row?.joiningDate != null) return {dojRaw: row.joiningDate, source: `initialDependents[${i}].joiningDate`}
      }
    }

    return {dojRaw: undefined, source: 'none'}
  }, [initialDependents])

  const getProrationFactor = (): number => {
    const {dojRaw} = resolveDojRaw()
    const dojValue = dojRaw
    const dojMs = parseDateOnlyToUtcMs(dojValue)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    // Only new joiners (DOJ after policy start) are prorated
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()

  const lastProrationDebugKeyRef = useRef<string>('')
  const lastPremiumDebugKeyRef = useRef<string>('')

  useEffect(() => {
    if (!prorationDebugEnabled) return

    const {dojRaw: dojValue, source: dojSource} = resolveDojRaw()
    const dojMs = parseDateOnlyToUtcMs(dojValue)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    const daysRemaining =
      dojMs !== null && expiryMs !== null ? daysBetweenInclusive(dojMs, expiryMs) : null

    const debugPayload = {
      grade: policyData?.Grade,
      dojRaw: dojValue,
      dojSource,
      effectiveRaw: policyEffectiveDate,
      expiryRaw: policyExpiryDate,
      dojMsUtc: dojMs,
      effectiveMsUtc: effectiveMs,
      expiryMsUtc: expiryMs,
      dojAfterEffective: dojMs !== null && effectiveMs !== null ? dojMs > effectiveMs : null,
      daysRemainingInclusive: daysRemaining,
      prorationFactor,
    }

    const key = JSON.stringify(debugPayload)
    if (key === lastProrationDebugKeyRef.current) return
    lastProrationDebugKeyRef.current = key

    // eslint-disable-next-line no-console
    console.log('[OnityEnrollment] Proration debug', debugPayload)
  }, [
    prorationDebugEnabled,
    resolveDojRaw,
    policyData,
    policyEffectiveDate,
    policyExpiryDate,
    prorationFactor,
  ])

  // Form for enrolling family members
  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [acceptedPremium, setAcceptedPremium] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedTopup, setSelectedTopup] = useState<TopupPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  // New dependent form state
  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: ''
  })

  const isGrade1 = policyData.Grade === 'Grade1'
  
  let baseSumInsured = 0
  if (policyData.Grade === 'Grade1') baseSumInsured = 400000
  else if (policyData.Grade === 'Grade2') baseSumInsured = 500000
  else if (policyData.Grade === 'Grade3') baseSumInsured = 750000

  const maxFreeDependents = isGrade1 ? 3 : 5
  // Total dependents excluding self
  const maxTotalDependents = 5

  useEffect(() => {
    // If preloaded members are passed (Frozen state from parent), use them directly
    if (preloadedMembers && preloadedMembers.length > 0) {
        setEnrolledMembers(preloadedMembers)
        setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
        // Map initialDependents to form state
        const mappedDependents = initialDependents.map((item, index) => {
            let relation = item.Relation;
            const rLower = relation ? relation.toLowerCase() : '';
            
            if (['husband', 'wife', 'spouse'].includes(rLower)) relation = 'Spouse';
            else if (['son', 'daughter', 'child'].includes(rLower)) relation = 'Child';
            else if (['father', 'mother', 'parent'].includes(rLower)) relation = 'Parent';
            else if (['brother', 'sister', 'sibling'].includes(rLower)) relation = 'Sibling';
            else if (rLower.includes('law')) relation = 'ParentInLaw';
            
            return {
                id: item.sid || Date.now() + index,
                name: item.Name,
                dob: item.DOB ? toISTInputFormat(item.DOB) : '',
                relation: relation,
                gender: item.Gender || ''
            }
        });
        setDependents(mappedDependents);
    } else {
        // Initialize with Employee as first dependent (Active state)
        setDependents([{
          id: 1, // Fixed ID for employee
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '' 
        }])
    }
  }, [policyData, preloadedMembers, initialDependents])

  // Removed internal fetch for frozen data since parent handles it now
  /* 
  useEffect(() => {
    if (policyData.freezeEnrolment) {
      fetchFrozenData()
    }
  }, [policyData])
  
  const fetchFrozenData = async () => ... 
  */

  const calculatePremium = () => {
    let dependentPremiumFullYear = 0
    let dependentCount = 0
    
    // Count dependents excluding Self
    const familyDependents = dependents.filter(d => d.relation !== 'Self')
    
    if (isGrade1) {
      const chargeableDependents = Math.max(0, familyDependents.length - maxFreeDependents)
      if (chargeableDependents > 0) {
        dependentPremiumFullYear = chargeableDependents * PREMIUM_PER_LIFE
        dependentCount = chargeableDependents
      }
    }

    let topupPremiumFullYear = 0
    if (selectedTopup) {
        // Dynamic Top-Up Premium Logic
        if (isGrade1 && familyDependents.length > maxFreeDependents) {
            // Use Extended premium for Grade 1 with > 3 dependents
        topupPremiumFullYear = selectedTopup.premiumExtended || selectedTopup.premium
        } else {
        topupPremiumFullYear = selectedTopup.premium
        }
    }

    // Prorate all premium for new joiners based on DOJ -> policy expiry
    const dependentPremium = dependentPremiumFullYear * prorationFactor
    const topupPremium = topupPremiumFullYear * prorationFactor
    
    const dependentGST = dependentPremium * GST_RATE
    const dependentTotal = dependentPremium + dependentGST
    
    const topupGST = topupPremium * GST_RATE
    const topupTotal = topupPremium + topupGST

    const totalTaxable = dependentPremium + topupPremium
    const totalGST = dependentGST + topupGST
    const totalPayable = totalTaxable + totalGST

    // Round only the final payable premium (salary deduction amount)
    const totalPayableRounded = Math.round(totalPayable)

    if (prorationDebugEnabled) {
      const premiumDebugPayload = {
        grade: policyData?.Grade,
        dependentsTotal: dependents.length,
        familyDependents: familyDependents.length,
        maxFreeDependents,
        dependentCountCharged: dependentCount,
        selectedTopup: selectedTopup
          ? {
              name: selectedTopup.name,
              sumInsured: selectedTopup.sumInsured,
              premium: selectedTopup.premium,
              premiumExtended: selectedTopup.premiumExtended,
            }
          : null,
        prorationFactor,
        dependentPremiumFullYear,
        topupPremiumFullYear,
        dependentPremiumProrated: dependentPremium,
        topupPremiumProrated: topupPremium,
        totalGst: totalGST,
        totalPayableRaw: totalPayable,
        totalPayableRounded,
      }

      const key = JSON.stringify(premiumDebugPayload)
      if (key !== lastPremiumDebugKeyRef.current) {
        lastPremiumDebugKeyRef.current = key
        // eslint-disable-next-line no-console
        console.log('[OnityEnrollment] Premium debug', premiumDebugPayload)
      }
    }

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      topupPremium,
      topupGST,
      topupTotal,
      gst: totalGST,
      total: totalPayableRounded
    }
  }

  const handleAddClick = () => {
    setError(null)
    const familyDependents = dependents.filter(d => d.relation !== 'Self')
    
    if (familyDependents.length >= maxTotalDependents) {
      setError(`Maximum of ${maxTotalDependents} dependents allowed.`)
      return
    }

    if (isGrade1 && familyDependents.length >= maxFreeDependents) {
      setShowConsentModal(true)
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: ''
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    const age = new Date().getFullYear() - new Date(dep.dob).getFullYear()

    // Age validations
    if ((dep.relation === 'Child' || dep.relation === 'Sibling') && age >= 25) {
      setError(`${dep.relation} must be below 25 years of age.`)
      return false
    }

    // Relation count validations
    const relationCounts = dependents.reduce((acc, curr) => {
      acc[curr.relation] = (acc[curr.relation] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    if (dep.relation === 'Spouse' && relationCounts['Spouse']) {
      setError('Only 1 Spouse allowed.')
      return false
    }

    if (dep.relation === 'Child' && (relationCounts['Child'] || 0) >= 2) {
      setError('Maximum 2 Children allowed.')
      return false
    }

    // Parent and ParentInLaw gender validation
    if (dep.relation === 'Parent' || dep.relation === 'ParentInLaw') {
      const hasSameGender = dependents.some(d => d.relation === dep.relation && d.gender === dep.gender)
      if (hasSameGender) {
        setError(`A ${dep.gender} ${dep.relation === 'ParentInLaw' ? 'Parent-in-law' : dep.relation} is already added.`)
        return false
      }
    }

    // Parent vs ParentInLaw validation
    const hasParents = dependents.some(d => d.relation === 'Parent')
    const hasInLaws = dependents.some(d => d.relation === 'ParentInLaw')

    if (dep.relation === 'Parent' && hasInLaws) {
      setError('Cannot add Parents when Parents-in-law are already added.')
      return false
    }

    if (dep.relation === 'ParentInLaw' && hasParents) {
      setError('Cannot add Parents-in-law when Parents are already added.')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    setDependents([...dependents, newDependent])
    setShowAddModal(false)
  }

  const removeDependent = (id: number) => {
    setDependents(dependents.filter(d => d.id !== id))
    setError(null)
  }

  const handleConsent = () => {
    setAcceptedPremium(true)
    setShowConsentModal(false)
    // Immediately open add modal after consent
    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: ''
    })
    setShowAddModal(true)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()
    
    // Map premium against each family member
    const familyMembersWithPremium = dependents.map((dep, index) => {
      let memberPremium = 0;
      
      // Logic for assigning premium
      // Self is always index 0. Family members start from index 1.
      if (dep.relation !== 'Self') {
        // Calculate the index relative to family members only (0-based for family)
        // Since dependents[0] is Self, the first family member is at dependents[1], so familyIndex = 0
        const familyIndex = index - 1;
        
        if (isGrade1) {
          // For Grade 1, first 'maxFreeDependents' are free. Subsequent ones are charged.
          if (familyIndex >= maxFreeDependents) {
            memberPremium = PREMIUM_PER_LIFE * prorationFactor;
          }
        }
      }

      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: memberPremium,
        Grade: policyData.Grade,
        crm_client_id: policyData.CorpCode,
        Email:  currentUser?.Email,
        CorporateCode: policyData.CorporateCode
      };

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: acceptedPremium,
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,
          
          // Topup details
          TopupName: selectedTopup?.name || '',
          topupsumInsured: selectedTopup?.sumInsured || 0,
          topup_base_premium: premiumData.topupPremium,
          topup_gst: premiumData.topupGST,
          topup_total_premium: premiumData.topupTotal,

          // Base (Dependent) details
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          // Grand totals
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        };
      }

      return baseMember;
    });

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch('https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(familyMembersWithPremium),
      });

      if (response.ok) {
         setShowSuccess(true);
      } else {
         setError('Failed to submit enrollment. Please try again.');
         setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e);
      setError('Network error occurred. Please try again.');
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()
  const availableTopups = TOPUP_PLANS.filter(p => p.grade === policyData.Grade)

  if (policyData.freezeEnrolment) {
    // If explicitly frozen or if we have preloaded members (which implies frozen/submitted state)
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0)) return <div className="text-center p-5 text-primary"><span className="spinner-border spinner-border-sm align-middle ms-2"></span> Loading enrollment details...</div>
    
    // Calculate totals from enrolled data (either from prop or state)
    const membersToDisplay = (preloadedMembers && preloadedMembers.length > 0) ? preloadedMembers : enrolledMembers
    
    const frozenPremium = {
        dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
        topupPremium: membersToDisplay.reduce((acc, m) => acc + (m.TopupBasePremium || 0), 0),
        gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
        total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    const optedTopupMember = membersToDisplay.find(m => m.TopupName)
    const optedTopup = optedTopupMember?.TopupName
    const optedTopupSI = optedTopupMember?.TopupSumInsured

    return (
        <div className='card'>
            <div className='card-body'>
                <Row>
                    <Col md={8}>
                       <div className="d-flex justify-content-between align-items-center mb-4">
                           <h3 className="mb-0">Enrolled Family Details</h3>
                           {baseSumInsured > 0 && <div className="badge badge-light-primary fs-6 p-3">Base Family Sum Insured: ₹{baseSumInsured.toLocaleString()}</div>}
                       </div>
                       <div className='table-responsive mb-5'>
                         <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                           <thead>
                             <tr className='fw-bolder text-muted'>
                               <th>Name</th>
                               <th>Relation</th>
                               <th>Gender</th>
                               <th>Date of Birth</th>
                             </tr>
                           </thead>
                           <tbody>
                             {membersToDisplay.map((member) => (
                               <tr key={member.Id}>
                                 <td>
                                    <div className='d-flex align-items-center'>
                                        <div className='d-flex justify-content-start flex-column'>
                                            <div className='text-dark fw-bolder fs-6'>
                                            {member.Name}
                                            </div>
                                        </div>
                                    </div>
                                 </td>
                                 <td>{member.Relation}</td>
                                 <td>{member.Gender}</td>
                                 <td>{formatDateIST(member.Dob)}</td>
                               </tr>
                             ))}
                           </tbody>
                         </Table>
                       </div>
                       
                       {availableTopups.length > 0 && (
                           <>
                                <h3 className="mb-3">Selected Top-up Plan</h3>
                                <div className="alert alert-info d-flex align-items-center mb-3">
                                   <i className="bi bi-info-circle-fill text-info fs-3 me-3"></i>
                                   <div className="text-gray-700 fw-bold fs-7">
                                      Top-Up coverage provides additional sum insured over and above base family for enrolled members
                                   </div>
                                </div>
                                <div className="p-4 bg-light rounded border border-gray-300">
                                    {optedTopup ? (
                                        <div className="fw-bold fs-5 text-gray-800">
                                            {optedTopup} {optedTopupSI ? `- Suminsured : ₹${optedTopupSI.toLocaleString()}` : ''}
                                        </div>
                                    ) : (
                                        <div className="text-muted">No Top-up plan selected.</div>
                                    )}
                                </div>
                            </>
                       )}
                    </Col>
                    <Col md={4}>
                        <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                           <h4 className='mb-4'>Premium Summary</h4>
                           
                           {frozenPremium.dependentPremium > 0 && (
                             <div className='mb-3'>
                               <strong>Additional Dependents Premium:</strong>
                               <div className='float-end'>₹{frozenPremium.dependentPremium.toLocaleString()}</div>
                             </div>
                           )}
                           {frozenPremium.topupPremium > 0 && (
                             <div className='mb-3'>
                               <strong>Top-up Premium:</strong>
                               <div className='float-end'>₹{frozenPremium.topupPremium.toLocaleString()}</div>
                             </div>
                           )}
                           
                           {(frozenPremium.total > 0) ? (
                            <>
                              <hr />
                              <div className='mb-2'>
                                <span>Taxable Amount:</span>
                                <span className='float-end'>₹{(frozenPremium.dependentPremium + frozenPremium.topupPremium).toLocaleString()}</span>
                              </div>
                              <div className='mb-2'>
                                <span>GST (18%):</span>
                                <span className='float-end'>₹{frozenPremium.gst.toLocaleString()}</span>
                              </div>
                              <hr />
                              <h5 className='text-primary'>
                                Total Payable:
                                <span className='float-end'>₹{frozenPremium.total.toLocaleString()}</span>
                              </h5>
                              <div className="text-muted fs-7 mt-2 text-end">
                                The total premium will be deducted from your next salary.
                              </div>
                            </>
                           ) : (
                             <div className='text-muted text-center py-5'>
                               No additional premium applicable.
                             </div>
                           )}
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    )
  }

  if (showSuccess) {
    return (
       <div className='card'>
        <div className='card-body text-center py-5'>
           <div className="mb-4">
              <i className="bi bi-check-circle-fill text-success fs-3x"></i>
           </div>
           <h2 className="text-success mb-4">Thank You!</h2>
           
           {premiumInfo.total > 0 ? (
             <div className="alert alert-info d-inline-block p-4">
                <span className="fs-4">
                   An amount of <strong>₹{premiumInfo.total.toLocaleString()}</strong> will be deducted from your salary.
                </span>
             </div>
           ) : (
             <div className="fs-4 text-gray-600">
                Your enrollment is complete.
             </div>
           )}
           
           {/* <div className="mt-5">
             <Button variant='primary' size="lg" onClick={onSuccess}>Close</Button>
           </div> */}
        </div>
       </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
                {error}
            </div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                  <h3 className="mb-0">Family Details</h3>
                  {baseSumInsured > 0 && <div className="text-primary fw-bold mt-1">Base Family Sum Insured: ₹{baseSumInsured.toLocaleString()}</div>}
              </div>
              <Button variant='primary' onClick={handleAddClick} disabled={dependents.filter(d => d.relation !== 'Self').length >= maxTotalDependents}>
                Add Dependent
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => {
                    return (
                      <tr key={dep.id} className='text-capitalize text-dark'>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                                {dep.name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {dep.relation}
                          </span>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {dep.gender}
                          </span>
                        </td>
                        <td>
                          <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                            {formatDateIST(dep.dob)}
                          </span>
                        </td>
                        <td>
                          {dep.relation !== 'Self' && (
                            <Button variant='danger' size='sm' onClick={() => removeDependent(dep.id)}>
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {availableTopups.length > 0 && (
              <div className='mb-5'>
                

                <h3>Optional Top-up Plans</h3>

                <div className="alert alert-warning d-flex align-items-center mb-4">
                     <i className="bi bi-info-circle-fill text-warning fs-3 me-3"></i>
                     <div className="text-gray-700 fw-bold">
                         Top-Up coverage provides additional sum insured for you and your family at an extra cost, if you choose this option, the total premium will be deducted from your next salary.
                     </div>
                </div>


                <Form.Group>
                  <Form.Check
                    type='radio'
                    label='No Top-up'
                    name='topupPlan'
                    id='topup-none'
                    checked={selectedTopup === null}
                    onChange={() => setSelectedTopup(null)}
                    className='mb-2'
                  />
                  {availableTopups.map((plan, idx) => {
                    // Logic to display correct premium based on current selection
                    const displayPremium = (isGrade1 && dependents.filter(d => d.relation !== 'Self').length > maxFreeDependents)
                       ? (plan.premiumExtended || plan.premium)
                       : plan.premium;
                    
                    return (
                        <div key={idx} className='d-flex align-items-center mb-2'>
                          <Form.Check
                            type='radio'
                            label={`${plan.name} (Sum Insured: ₹${plan.sumInsured.toLocaleString()}) - Premium: ₹${displayPremium.toLocaleString()}`}
                            name='topupPlan'
                            id={`topup-${idx}`}
                            checked={selectedTopup?.name === plan.name}
                            onChange={() => setSelectedTopup(plan)}
                          />
                        </div>
                    )
                  })}
                </Form.Group>
              </div>
            )}
          </Col>

          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>
              
              {premiumInfo.dependentCount > 0 && (
                <div className='mb-3'>
                  <strong>Additional Dependents ({premiumInfo.dependentCount}):</strong>
                  <div className='float-end'>₹{premiumInfo.dependentPremium.toLocaleString()}</div>
                </div>
              )}

              {selectedTopup && (
                <div className='mb-3'>
                  <strong>Top-up Premium:</strong>
                  <div className='float-end'>₹{premiumInfo.topupPremium.toLocaleString()}</div>
                </div>
              )}

              {(premiumInfo.dependentCount > 0 || selectedTopup) ? (
                <>
                  <hr />
                  <div className='mb-2'>
                    <span>Taxable Amount:</span>
                    <span className='float-end'>₹{(premiumInfo.dependentPremium + premiumInfo.topupPremium).toLocaleString()}</span>
                  </div>
                  <div className='mb-2'>
                    <span>GST (18%):</span>
                    <span className='float-end'>₹{premiumInfo.gst.toLocaleString()}</span>
                  </div>
                  <hr />
                  <h5 className='text-primary'>
                    Total Payable:
                    <span className='float-end'>₹{premiumInfo.total.toLocaleString()}</span>
                  </h5>
                  <div className="text-muted fs-7 mt-2 text-end">
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  No additional premium applicable.
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button variant='success' size='lg' onClick={() => setShowConfirmModal(true)} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add Dependent Modal */}
      <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setError(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Add Dependent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
                {error}
            </div>
          )}
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type='text' 
                value={newDependent.name} 
                onChange={(e) => setNewDependent({...newDependent, name: e.target.value})}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select 
                value={newDependent.relation}
                onChange={(e) => setNewDependent({...newDependent, relation: e.target.value})}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select 
                value={newDependent.gender}
                onChange={(e) => setNewDependent({...newDependent, gender: e.target.value})}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control 
                type='date' 
                value={newDependent.dob} 
                onChange={(e) => setNewDependent({...newDependent, dob: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant='primary' onClick={saveDependent}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Final Verification Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning d-flex align-items-center">
             <span className="svg-icon svg-icon-2hx svg-icon-warning me-4">
                 <i className="bi bi-exclamation-triangle-fill fs-2"></i>
             </span>
             <div className="d-flex flex-column">
                 {premiumInfo.total > 0 && (
                   <>
                     <div className="fw-bold mb-1">Total Premium Deduction: ₹{premiumInfo.total.toLocaleString()}</div>
                     <div className="small">This amount will be debited from your salary.</div>
                   </>
                 )}
                 <div className="small mt-1"><strong>Please review and confirm your nominations carefully. Once submitted, no changes can be made in the policy year.</strong></div>
             </div>
          </div>
          <p className="mb-0 mt-3">Do you want to proceed to enroll your family?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button variant='primary' onClick={() => {
              setShowConfirmModal(false);
              handleSubmit();
          }} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Family'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Premium Consent Modal */}
      <Modal show={showConsentModal} onHide={() => setShowConsentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Additional Premium Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info d-flex align-items-center">
              <span className="svg-icon svg-icon-2hx svg-icon-info me-4">
                 <i className="bi bi-info-circle-fill fs-2"></i>
              </span>
              <div className="d-flex flex-column">
                  <p className="mb-2">You are eligible to add a 4th and/or 5th dependent under the policy.</p>
                  <p className="mb-0"><strong>An additional premium of ₹{PREMIUM_PER_LIFE.toLocaleString()} + 18% GST per life will be applicable for each additional dependent.</strong></p>
              </div>
          </div>
          <p className="mt-3">Do you consent to pay this additional premium?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConsentModal(false)}>No, I do not agree</Button>
          <Button variant='primary' onClick={handleConsent}>Yes, I agree</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
